import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import MarketingLayout from "@/components/MarketingLayout";
import AIOrbHero from "@/components/AIOrbHero";
import AnimatedToolIcon from "@/components/AnimatedToolIcon";
import HoloGrid from "@/components/HoloGrid";
import Seo, { OrganizationJsonLd } from "@/components/Seo";
import { api } from "@/lib/api";
import * as Lucide from "lucide-react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/60c761e9-4586-45ac-b6e8-317b7868af83/images/bd9af47f54f7de3d5263a9e95364e21022305f062297c151ef9af82981ea3f41.png";

const CATEGORY_FILTERS = ["All", "Content Writing", "SEO", "Marketing", "Image", "Business", "Development", "Web"];

function IconFromName({ name, className = "" }) {
  const Cmp = Lucide[name] || Lucide.Sparkles;
  return <Cmp className={className} size={20} />;
}

export default function Home() {
  const [tools, setTools] = useState([]);
  const [cat, setCat] = useState("All");

  useEffect(() => {
    api.get("/tools").then((r) => setTools(r.data)).catch(() => {});
  }, []);

  const visible = tools.filter((t) => cat === "All" || t.category === cat).slice(0, 12);

  return (
    <MarketingLayout>
      <Seo path="/" />
      <OrganizationJsonLd />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <HoloGrid />
        <div className="absolute inset-0 pointer-events-none">
          <motion.div
            className="absolute -top-20 -left-20 w-[480px] h-[480px] rounded-full bg-purple-600/20 blur-3xl"
            animate={{ x: [0, 30, 0], y: [0, 20, 0] }}
            transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute top-20 right-0 w-[420px] h-[420px] rounded-full bg-blue-600/15 blur-3xl"
            animate={{ x: [0, -30, 0], y: [0, -20, 0] }}
            transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-12 pb-20 grid lg:grid-cols-2 gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="bx-chip"><Lucide.Sparkles size={12} /> ALL-IN-ONE AI PLATFORM</div>
            <h1 className="bx-heading mt-5 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05]">
              All Powerful <span className="bx-gradient-text">AI Tools</span>,<br />
              One Smart Platform.
            </h1>
            <p className="mt-5 text-slate-300 text-lg max-w-xl">
              Access 25+ AI tools with one account and one universal credit balance. Save time, boost productivity, and grow your business with <strong className="text-white">BINAREX.CO</strong>.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-get-started" className="bx-btn-primary">Get Started Free <Lucide.ArrowRight size={16} /></Link>
              <Link to="/tools" data-testid="hero-explore-tools" className="bx-btn-ghost">Explore AI Tools</Link>
            </div>
            <div className="mt-10 grid grid-cols-4 gap-4 max-w-md">
              {[
                ["25+", "AI Tools"],
                ["100K+", "Happy Users"],
                ["1M+", "Generations"],
                ["99.9%", "Uptime"],
              ].map(([n, l]) => (
                <div key={l}>
                  <div className="bx-heading text-2xl font-extrabold bx-gradient-text">{n}</div>
                  <div className="text-xs text-slate-400 mt-1">{l}</div>
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }}>
            <AIOrbHero src={HERO_IMG} />
          </motion.div>
        </div>
      </section>

      {/* Marquee strip */}
      <section className="relative overflow-hidden border-y border-white/10 bg-[#06060D]/70 py-4">
        <motion.div
          className="flex gap-12 whitespace-nowrap"
          animate={{ x: ["0%", "-50%"] }}
          transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
        >
          {[...Array(2)].map((_, dup) => (
            <div key={dup} className="flex gap-12 items-center text-slate-400 text-sm">
              {["GPT-5", "Nano Banana", "Sora 2", "Claude Sonnet", "Whisper", "Gemini 3", "Stable Diffusion", "OpenAI", "Anthropic", "DALL·E"].map((t) => (
                <span key={t + dup} className="inline-flex items-center gap-2">
                  <Lucide.Sparkle size={12} className="text-purple-300" /> {t}
                </span>
              ))}
            </div>
          ))}
        </motion.div>
      </section>

      {/* TOOLS GRID */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-14">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="bx-heading text-3xl sm:text-4xl font-bold">Powerful AI Tools For Every Need</h2>
          <p className="text-slate-400 mt-3">One platform. Multiple AI tools. Infinite possibilities.</p>
        </div>

        <div className="mt-8 flex flex-wrap justify-center gap-2">
          {CATEGORY_FILTERS.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`home-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-4 py-2 rounded-full text-sm transition ${
                cat === c ? "bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-[0_8px_24px_-10px_rgba(139,92,246,0.6)]" : "bg-white/5 border border-white/10 text-slate-300 hover:bg-white/10"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {visible.map((t, i) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.4, delay: (i % 8) * 0.05 }}
            >
              <Link
                to={`/tools/${t.slug}`}
                data-testid={`home-tool-${t.slug}`}
                className="bx-card p-5 group block"
              >
                <AnimatedToolIcon name={t.icon} accent={["", "cyan", "blue", "emerald", "pink", "amber"][i % 6]} />
                <div className="mt-4 bx-heading text-lg font-semibold group-hover:text-white">{t.name}</div>
                <p className="text-sm text-slate-400 mt-1 line-clamp-2">{t.description}</p>
                <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
                  <span>{t.category}</span>
                  <span className="text-purple-300 font-mono">{t.credit_cost} credits</span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link to="/tools" data-testid="home-view-all-tools" className="bx-btn-primary">View All Tools <Lucide.ArrowRight size={16} /></Link>
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-14">
        <div className="grid md:grid-cols-4 gap-4">
          {[
            { i: "Layers", t: "One Platform", d: "Access the best AI models in one place." },
            { i: "ShieldCheck", t: "Secure & Private", d: "Enterprise-grade security standards." },
            { i: "Wallet", t: "Save Time & Money", d: "10x productivity with AI automation." },
            { i: "Headset", t: "24/7 Support", d: "Got questions? We're here to help." },
          ].map((f, i) => (
            <div key={i} className="bx-card p-5">
              <span className={`bx-glow-icon ${["", "cyan", "blue", "emerald"][i % 4]}`}><IconFromName name={f.i} /></span>
              <div className="bx-heading mt-3 font-semibold">{f.t}</div>
              <p className="text-sm text-slate-400 mt-1">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
        <div className="rounded-3xl border border-white/10 p-10 lg:p-14 text-center bg-gradient-to-br from-purple-600/15 via-blue-600/10 to-transparent">
          <h2 className="bx-heading text-3xl sm:text-4xl font-bold">Start building with AI in under 60 seconds</h2>
          <p className="text-slate-300 mt-3 max-w-2xl mx-auto">Sign up free, get 200 trial credits, and try every tool. No credit card required.</p>
          <div className="mt-6 flex justify-center gap-3 flex-wrap">
            <Link to="/register" className="bx-btn-primary">Create Free Account</Link>
            <Link to="/pricing" className="bx-btn-ghost">See Pricing</Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
